/* eslint-disable new-cap */
import { BlueprintInstanceType } from '../shared.enums';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { Publish } from '../../decorators';
import { SkuAnnotation } from './sku-annotation.class';
import { SkuAnnotationData, SkuAnnotationsDataIn, SkuAnnotationsDataOut } from './types';

export class SkuAnnotations extends KitchenDesignBase<SkuAnnotationsDataIn> {
	#contextEmail: string;
	readonly children: Array<SkuAnnotation>;

	constructor (contextEmail: string, options: KitchenDesignOptions<SkuAnnotationsDataIn> = {}) {
		super(BlueprintInstanceType.SkuAnnotations, options);
		this.#contextEmail = contextEmail;

		this.children = [];
		if (options.dataIn && options.dataIn.children && options.dataIn.children.length > 0) {
			options.dataIn.children.forEach((a) => this.children.push(new SkuAnnotation(contextEmail, null, { dataIn: a, parent: this })));
		}
		this.setInitialized();
	}

	@Publish('sku_annotations', 'added')
	public add (dataIn: Partial<SkuAnnotationData>): string {
		const userEmail = dataIn?.createdBy ?? this.#contextEmail;
		// parent link so property-setter @Publish events bubble to the collection and on to the kitchen
		const newAnnotation = new SkuAnnotation(userEmail, dataIn, { parent: this });
		this.children.push(newAnnotation);
		return newAnnotation.id;
	}

	@Publish('sku_annotations', 'deleted')
	public delete (id: string): boolean {
		const index = this.children.findIndex((annotation) => annotation.id === id);
		if (index !== -1) {
			this.children.splice(index, 1);
			return true;
		}
		return false;
	}

	public findById (id: string): SkuAnnotation | null {
		return this.children.find((annotation) => annotation.id === id) ?? null;
	}

	// Z-order is the array position -- later entries render on top. Persisted for free via serialize()'s array order.
	@Publish('sku_annotations', 'reordered')
	public moveToFront (id: string): boolean {
		const index = this.children.findIndex((annotation) => annotation.id === id);
		if (index === -1) return false;
		const [moved] = this.children.splice(index, 1);
		this.children.push(moved);
		return true;
	}

	@Publish('sku_annotations', 'reordered')
	public moveToBack (id: string): boolean {
		const index = this.children.findIndex((annotation) => annotation.id === id);
		if (index === -1) return false;
		const [moved] = this.children.splice(index, 1);
		this.children.unshift(moved);
		return true;
	}

	public serialize (): SkuAnnotationsDataOut {
		return {
			children: this.children.map((annotation: SkuAnnotation) => annotation.serialize())
		};
	}
}
